import { useEffect, useMemo, useState } from 'react';

import { useHypotheses } from '@/hooks/useHypotheses';
import { useStellaRecommendations } from '@/hooks/useStellaRecommendations';
import { useToolVersion } from '@/hooks/useToolVersion';
import type { PageContext } from '@/types/pageContext';
import type { ToolData } from '@/types/toolVersion';

import type { ShortcutCondition } from '@/components/chat/shortcutsConfig';

export type ShortcutConditionData = Record<ShortcutCondition, boolean>;

const TOOLS_LIST_VISITED_KEY = 'stella-visited-tools-list';

const DEFAULT_CONDITIONS: ShortcutConditionData = {
  always: true,
  populated: false,
  'has-hypotheses': false,
  'no-hypotheses': false,
  'health-score-gte-30': false,
  'low-maturity': false,
  'multiple-versions': false,
  'first-visit': false,
};

interface ConditionContext {
  hypotheses: ReturnType<typeof useHypotheses>['data'];
  isHypothesesLoading: boolean;
  isHypothesesError: boolean;
  currentVersion: ReturnType<typeof useToolVersion>['currentVersion'];
  versions: ReturnType<typeof useToolVersion>['versions'];
  isFirstVisitToToolsList: boolean;
  healthScore: number;
  isHealthScoreLoading: boolean;
  isHealthScoreError: boolean;
  hasContentFromContext: boolean;
  healthScoreFromContext: number | undefined;
}

type ConditionEvaluator = (
  ctx: ConditionContext
) => Partial<ShortcutConditionData>;

const isNonEmptyString = (value: unknown): boolean =>
  typeof value === 'string' && value.trim().length > 0;

const METADATA_KEYS = new Set([
  'id',
  'order',
  'sort_order',
  'product_id',
  'tool_id',
  'version_id',
  'parent_id',
  'created_at',
  'updated_at',
]);

const hasUserContent = (obj: Record<string, unknown>): boolean =>
  Object.entries(obj).some(
    ([key, value]) => !METADATA_KEYS.has(key) && isNonEmptyString(value)
  );

const isArrayPopulated = (arr: unknown[]): boolean => {
  return arr.some((item) => {
    if (isNonEmptyString(item)) {
      return true;
    }

    if (item !== null && typeof item === 'object' && !Array.isArray(item)) {
      return hasUserContent(item as Record<string, unknown>);
    }

    return false;
  });
};

export const isToolDataPopulated = (
  toolData: ToolData | null | undefined
): boolean => {
  if (!toolData || typeof toolData !== 'object' || Array.isArray(toolData)) {
    return false;
  }

  return Object.values(toolData as Record<string, unknown>).some((value) => {
    if (isNonEmptyString(value)) {
      return true;
    }

    if (Array.isArray(value)) {
      return isArrayPopulated(value);
    }

    return false;
  });
};

const evaluateProductConditions = ({
  healthScore,
  isHealthScoreLoading,
  isHealthScoreError,
  healthScoreFromContext,
}: ConditionContext): Partial<ShortcutConditionData> => {
  const effectiveScore = healthScoreFromContext ?? healthScore;
  const isLoading =
    healthScoreFromContext === undefined &&
    (isHealthScoreLoading || isHealthScoreError);

  if (isLoading) {
    return {};
  }

  const healthScoreGte30 = effectiveScore >= 30;

  return {
    'health-score-gte-30': healthScoreGte30,
    'low-maturity': !healthScoreGte30,
  };
};

const isVersionPopulated = (
  version: ConditionContext['currentVersion']
): boolean => {
  if (!version) {
    return false;
  }

  return isToolDataPopulated(version.tool_data);
};

const evaluateToolConditions = ({
  currentVersion,
  hasContentFromContext,
}: ConditionContext): Partial<ShortcutConditionData> => {
  const populated = hasContentFromContext || isVersionPopulated(currentVersion);

  return {
    populated,
  };
};

const evaluateToolsListConditions = ({
  isFirstVisitToToolsList,
}: ConditionContext): Partial<ShortcutConditionData> => {
  return {
    'first-visit': isFirstVisitToToolsList,
  };
};

const evaluateToolVersionsListConditions = ({
  versions,
}: ConditionContext): Partial<ShortcutConditionData> => {
  const hasMultipleVersions = versions.length >= 2;

  return {
    'multiple-versions': hasMultipleVersions,
  };
};

const evaluateExperimentsListConditions = ({
  hypotheses,
  isHypothesesLoading,
  isHypothesesError,
}: ConditionContext): Partial<ShortcutConditionData> => {
  if (isHypothesesLoading || isHypothesesError) {
    return {};
  }

  const hasHypotheses = (hypotheses?.length ?? 0) > 0;

  return {
    'has-hypotheses': hasHypotheses,
    'no-hypotheses': !hasHypotheses,
  };
};

const CONDITION_EVALUATORS: Partial<
  Record<PageContext['pageType'], ConditionEvaluator>
> = {
  product: evaluateProductConditions,
  tool: evaluateToolConditions,
  'tools-list': evaluateToolsListConditions,
  'tool-versions-list': evaluateToolVersionsListConditions,
  'experiments-list': evaluateExperimentsListConditions,
};

const useFirstVisitToToolsList = (isToolsListPage: boolean): boolean => {
  const [isFirstVisit, setIsFirstVisit] = useState(
    () =>
      isToolsListPage && localStorage.getItem(TOOLS_LIST_VISITED_KEY) === null
  );

  useEffect(() => {
    if (!isToolsListPage) {
      setIsFirstVisit(false);
      return;
    }

    setIsFirstVisit(localStorage.getItem(TOOLS_LIST_VISITED_KEY) === null);
    localStorage.setItem(TOOLS_LIST_VISITED_KEY, '1');
  }, [isToolsListPage]);

  return isFirstVisit;
};

const getToolId = (pageContext: PageContext | null): string => {
  if (pageContext?.pageType === 'tool') {
    return pageContext.toolId;
  }

  if (pageContext?.pageType === 'tool-versions-list') {
    return pageContext.toolId;
  }

  return '';
};

export const useShortcutConditionData = (
  pageContext: PageContext | null
): ShortcutConditionData => {
  const productId =
    pageContext?.pageType === 'product' ? pageContext.productId : '';
  const toolId = getToolId(pageContext);
  const versionId =
    pageContext?.pageType === 'tool' ? pageContext.versionId : undefined;

  const isToolsListPage = pageContext?.pageType === 'tools-list';
  const isFirstVisitToToolsList = useFirstVisitToToolsList(isToolsListPage);

  const { data: stellaData } = useStellaRecommendations(productId);
  const {
    data: hypotheses,
    isLoading: isHypothesesLoading,
    isError: isHypothesesError,
  } = useHypotheses(undefined, {
    enabled: pageContext?.pageType === 'experiments-list',
  });
  const { currentVersion, versions } = useToolVersion({ toolId, versionId });

  return useMemo(() => {
    if (!pageContext) {
      return DEFAULT_CONDITIONS;
    }

    const hasContentFromContext =
      pageContext.pageType === 'tool' && pageContext.hasContent === true;

    const healthScoreFromContext =
      pageContext.pageType === 'product' ? pageContext.healthScore : undefined;

    const ctx: ConditionContext = {
      hypotheses,
      isHypothesesLoading,
      isHypothesesError,
      currentVersion,
      versions,
      isFirstVisitToToolsList,
      healthScore: stellaData?.healthScore?.score ?? 0,
      isHealthScoreLoading: stellaData?.isLoading ?? true,
      isHealthScoreError: stellaData?.error != null,
      hasContentFromContext,
      healthScoreFromContext,
    };

    return {
      ...DEFAULT_CONDITIONS,
      ...(CONDITION_EVALUATORS[pageContext.pageType]?.(ctx) ?? {}),
    };
  }, [
    pageContext,
    stellaData,
    hypotheses,
    isHypothesesLoading,
    isHypothesesError,
    currentVersion,
    versions,
    isFirstVisitToToolsList,
  ]);
};
